import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { NoteState } from "../../context/notes/NoteState";
import ClientPortalServices from "../../UserServices/ClientPortalServices";
import logo from "../../icons/logo.png";
import "./ClientPortal.css";

function fmt(v) {
  if (!v) return "—";
  const s = String(v).trim();
  // Already DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) return s.slice(0, 10);
  // ISO / DB: YYYY-MM-DD or datetime
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s.slice(0, 10);
}

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function Accordion({ id, title, count, icon, open, onToggle, tone = "red", children }) {
  return (
    <section className={`cp-acc cp-acc-${tone} ${open ? "is-open" : ""}`}>
      <button type="button" className="cp-acc-head" onClick={() => onToggle(id)}>
        <span className="cp-acc-left">
          <span className="cp-acc-ico" aria-hidden>
            {icon}
          </span>
          <span className="cp-acc-title">{title}</span>
          {count !== undefined && count !== null ? (
            <span className="cp-acc-count">{count}</span>
          ) : null}
        </span>
        <span className="cp-acc-chevron">{open ? "−" : "+"}</span>
      </button>
      {open ? <div className="cp-acc-body">{children}</div> : null}
    </section>
  );
}

function ScrollTable({ children, empty }) {
  if (empty) {
    return <div className="cp-empty">{empty}</div>;
  }
  return <div className="cp-scroll">{children}</div>;
}

export default function ClientDashboard() {
  const { state, dispatch: ctxDispatch } = useContext(NoteState);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({
    profile: true,
    active: true,
    history: false,
    gym: false,
    sessions: false,
    payments: false,
    freezing: false,
    cards: false,
    assessments: false,
  });

  useEffect(() => {
    const isClient =
      String(state.role) === "client" || state.accountType === "client";
    if (!isClient) {
      navigate("/");
      return;
    }
    if (state.mustChangePassword) {
      navigate("/client-change-password");
      return;
    }
    ClientPortalServices.dashboard()
      .then((res) => {
        setData(res.data?.data || null);
      })
      .catch((err) => {
        if (err.response?.data?.code === "MUST_CHANGE_PASSWORD") {
          navigate("/client-change-password");
          return;
        }
        toast.error(err.response?.data?.message || "Failed to load dashboard");
      })
      .finally(() => setLoading(false));
  }, [state.role, state.mustChangePassword, navigate]);

  const logout = async () => {
    try {
      await ClientPortalServices.logout();
    } catch (_) {
      /* ignore */
    }
    ctxDispatch({ type: "LOGOUT" });
    navigate("/");
  };

  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  const profile = data?.profile || {};
  const counts = data?.counts || {};
  const initials = useMemo(() => {
    const a = (profile.first_name || "?").trim().charAt(0);
    const b = (profile.last_name || "").trim().charAt(0);
    return (a + b).toUpperCase();
  }, [profile.first_name, profile.last_name]);

  const activePkgs = data?.active_packages || [];
  const historyPkgs = data?.package_history || [];
  const gym = data?.gym_attendance || [];
  const sessions = data?.session_attendance || [];
  const payments = data?.payments || [];
  const freezing = data?.freezing || [];
  const cards = data?.cards || [];
  const assessments = data?.assessments || {};
  const pre = assessments.pre || [];
  const post = assessments.post || [];

  return (
    <div className="cp-dash">
      <div className="cp-hero">
        <header className="cp-top">
          <div className="cp-brand">
            <img src={logo} alt="Vostro World" />
            <div>
              <strong>Member Portal</strong>
              <div className="cp-tagline">Health · Fitness · Wellness</div>
            </div>
          </div>
          <div className="cp-top-actions">
            <Link to="/client-change-password" className="cp-link-btn">
              Change password
            </Link>
            <button type="button" className="cp-btn outline" onClick={logout}>
              Sign out
            </button>
          </div>
        </header>

        {!loading && data ? (
          <div className="cp-welcome">
            <div className="cp-avatar">{initials}</div>
            <div>
              <h1>
                Welcome, {profile.first_name || "Member"}
              </h1>
              <p>
                {profile.uid || "—"}
                {profile.branch_name ? ` · ${profile.branch_name}` : ""}
                {profile.type ? ` · ${profile.type}` : ""}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="cp-center">Loading your membership…</p>
      ) : !data ? (
        <p className="cp-center">No data available.</p>
      ) : (
        <div className="cp-body">
          <div className="cp-stats">
            <div className="cp-stat red">
              <span>Active packages</span>
              <strong>{counts.active_packages ?? activePkgs.length}</strong>
            </div>
            <div className="cp-stat teal">
              <span>Gym check-ins</span>
              <strong>{counts.gym_checkins ?? gym.length}</strong>
            </div>
            <div className="cp-stat amber">
              <span>Sessions</span>
              <strong>{counts.sessions_delivered ?? sessions.length}</strong>
            </div>
            <div className="cp-stat blue">
              <span>Payments</span>
              <strong>{counts.payments ?? payments.length}</strong>
            </div>
          </div>

          <Accordion
            id="profile"
            title="My profile"
            icon="P"
            open={open.profile}
            onToggle={toggle}
            tone="red"
          >
            <div className="cp-grid">
              <div>
                <label>Member ID</label>
                <div>{profile.uid || "—"}</div>
              </div>
              <div>
                <label>Full name</label>
                <div>
                  {profile.full_name ||
                    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
                    "—"}
                </div>
              </div>
              <div>
                <label>Email</label>
                <div>{profile.email || "—"}</div>
              </div>
              <div>
                <label>Phone</label>
                <div>{profile.phone || "—"}</div>
              </div>
              <div>
                <label>Branch</label>
                <div>{profile.branch_name || profile.branch_id || "—"}</div>
              </div>
              <div>
                <label>Gender</label>
                <div>{profile.gender || "—"}</div>
              </div>
              <div>
                <label>Birthday</label>
                <div>{fmt(profile.birthday)}</div>
              </div>
              <div>
                <label>Membership type</label>
                <div>{profile.type || "—"}</div>
              </div>
              <div>
                <label>Registration package</label>
                <div>{profile.registration_package || "—"}</div>
              </div>
              <div>
                <label>Joined</label>
                <div>{fmt(profile.joining_date || profile.registration_date)}</div>
              </div>
              <div>
                <label>CNIC / ID</label>
                <div>{profile.cnic || "—"}</div>
              </div>
              <div>
                <label>Address</label>
                <div>{profile.address || "—"}</div>
              </div>
            </div>
          </Accordion>

          <Accordion
            id="active"
            title="Active packages"
            count={activePkgs.length}
            icon="A"
            open={open.active}
            onToggle={toggle}
            tone="teal"
          >
            <ScrollTable empty={activePkgs.length === 0 ? "No active packages right now." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Category</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Sessions</th>
                    <th>Freeze</th>
                  </tr>
                </thead>
                <tbody>
                  {activePkgs.map((p) => (
                    <tr key={p.order_detail_id}>
                      <td>
                        <strong>{p.package_name}</strong>
                        {p.trainer_name ? (
                          <div className="cp-sub">Trainer: {p.trainer_name}</div>
                        ) : null}
                      </td>
                      <td>{p.category}</td>
                      <td>{fmt(p.start_date)}</td>
                      <td>{fmt(p.end_date)}</td>
                      <td>
                        {p.sessions_total != null
                          ? `${p.sessions_delivered ?? 0}/${p.sessions_total}`
                          : "—"}
                      </td>
                      <td>
                        <span className="cp-pill">{p.freeze_status || "Active"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="history"
            title="Package history"
            count={historyPkgs.length}
            icon="H"
            open={open.history}
            onToggle={toggle}
            tone="amber"
          >
            <ScrollTable empty={historyPkgs.length === 0 ? "No package history." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Category</th>
                    <th>Sale</th>
                    <th>End</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {historyPkgs.map((p) => (
                    <tr key={`h-${p.order_detail_id}`}>
                      <td>{p.package_name}</td>
                      <td>{p.category}</td>
                      <td>{fmt(p.sale_date)}</td>
                      <td>{fmt(p.end_date)}</td>
                      <td>{money(p.net_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="gym"
            title="Gym attendance"
            count={gym.length}
            icon="G"
            open={open.gym}
            onToggle={toggle}
            tone="blue"
          >
            <ScrollTable empty={gym.length === 0 ? "No gym check-ins yet." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {gym.map((a) => (
                    <tr key={a.id}>
                      <td>{fmt(a.date)}</td>
                      <td>{a.checkin_time_12h || a.checkin_time_24h || "—"}</td>
                      <td>{a.checkout_time_12h || a.checkout_time_24h || "—"}</td>
                      <td>{a.attendance_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="sessions"
            title="Session attendance"
            count={sessions.length}
            icon="S"
            open={open.sessions}
            onToggle={toggle}
            tone="red"
          >
            <ScrollTable empty={sessions.length === 0 ? "No session attendance yet." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Package</th>
                    <th>Trainer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td>{fmt(s.date)}</td>
                      <td>{s.package_name}</td>
                      <td>{s.trainer_name || "—"}</td>
                      <td>
                        <span
                          className={`cp-pill ${
                            s.validate_status === "1" ? "ok" : "warn"
                          }`}
                        >
                          {s.validate_status === "1" ? "Validated" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="payments"
            title="Payments"
            count={payments.length}
            icon="Rs"
            open={open.payments}
            onToggle={toggle}
            tone="teal"
          >
            <ScrollTable empty={payments.length === 0 ? "No payments found." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Received</th>
                    <th>Method</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td>{fmt(p.date)}</td>
                      <td>{money(p.received)}</td>
                      <td>{p.payment_method || "—"}</td>
                      <td>{p.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="freezing"
            title="Freezing history"
            count={freezing.length}
            icon="F"
            open={open.freezing}
            onToggle={toggle}
            tone="blue"
          >
            <ScrollTable empty={freezing.length === 0 ? "No freezing records." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {freezing.map((f) => (
                    <tr key={f.id}>
                      <td>{fmt(f.start_date)}</td>
                      <td>{fmt(f.end_date)}</td>
                      <td>{f.duration || "—"}</td>
                      <td>{f.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="cards"
            title="Access cards"
            count={cards.length}
            icon="C"
            open={open.cards}
            onToggle={toggle}
            tone="amber"
          >
            <ScrollTable empty={cards.length === 0 ? "No cards assigned." : null}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Card #</th>
                    <th>Status</th>
                    <th>Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((c) => (
                    <tr key={c.id}>
                      <td>{c.number || c.id}</td>
                      <td>{c.status || "—"}</td>
                      <td>{fmt(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          </Accordion>

          <Accordion
            id="assessments"
            title="Assessments"
            count={pre.length + post.length}
            icon="As"
            open={open.assessments}
            onToggle={toggle}
            tone="red"
          >
            <div className="cp-two-col">
              <div>
                <h3>Pre-assessment</h3>
                <ScrollTable empty={pre.length === 0 ? "None on file." : null}>
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pre.map((r) => (
                        <tr key={`pre-${r.id}`}>
                          <td>{fmt(r.created_at)}</td>
                          <td>{r.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollTable>
              </div>
              <div>
                <h3>Post-assessment</h3>
                <ScrollTable empty={post.length === 0 ? "None on file." : null}>
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {post.map((r) => (
                        <tr key={`post-${r.id}`}>
                          <td>{fmt(r.created_at)}</td>
                          <td>{r.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollTable>
              </div>
            </div>
          </Accordion>

          <p className="cp-foot">
            Vostro World Member Portal · read-only view of your membership
          </p>
        </div>
      )}
    </div>
  );
}
